/**
 * Utilitaires pour l'administration des assets
 */

import type { Entity } from '@/api/types/core/common';

/**
 * Exporte les données au format JSON
 */
export const exportToJSON = <T extends Entity>(data: T[], filename: string): void => {
  const jsonData = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonData], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Exporte les données au format CSV
 */
export const exportToCSV = <T extends Entity>(data: T[], filename: string, columns: string[]): void => {
  if (data.length === 0) return;

  // En-têtes CSV
  const headers = columns.join(',');
  
  // Données CSV
  const csvData = data.map((item: T): string => {
    return columns.map((column: string): string => {
      const value = (item as Record<string, unknown>)[column];
      // Échapper les guillemets et virgules
      if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value?.toString() ?? '';
    }).join(',');
  }).join('\n');

  const csvContent = `${headers}\n${csvData}`;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Importe des données depuis un fichier JSON
 */
export const importFromJSON = <T>(file: File): Promise<T[]> => {
  return new Promise((resolve, reject): void => {
    const reader = new FileReader();
    
    reader.onload = (event): void => {
      try {
        const jsonData = JSON.parse(event.target?.result as string);
        if (Array.isArray(jsonData)) {
          resolve(jsonData);
        } else {
          reject(new Error('Le fichier JSON doit contenir un tableau'));
        }
      } catch (error) {
        reject(new Error('Erreur lors de la lecture du fichier JSON'));
      }
    };
    
    reader.onerror = (): void => reject(new Error('Erreur lors de la lecture du fichier'));
    reader.readAsText(file);
  });
};

/**
 * Importe des données depuis un fichier CSV
 */
export const importFromCSV = (file: File, columns: string[]): Promise<Record<string, string>[]> => {
  return new Promise((resolve, reject): void => {
    const reader = new FileReader();
    
    reader.onload = (event): void => {
      try {
        const csvContent = event.target?.result as string;
        const lines = csvContent.split('\n').filter((line: string): boolean => line.trim().length > 0);
        
        if (lines.length < 2) {
          reject(new Error('Le fichier CSV doit contenir au moins une ligne d\'en-tête et une ligne de données'));
          return;
        }
        
        const headers = lines[0].split(',').map((h: string): string => h.trim().replace(/"/g, ''));
        const data = lines.slice(1).map((line: string): Record<string, string> => {
          const values = line.split(',').map((v: string): string => v.trim().replace(/"/g, ''));
          const item: Record<string, string> = {};
          
          headers.forEach((header: string, index: number): void => {
            if (columns.includes(header)) {
              item[header] = values[index] || '';
            }
          });
          
          return item;
        });
        
        resolve(data);
      } catch (error) {
        reject(new Error('Erreur lors de la lecture du fichier CSV'));
      }
    };
    
    reader.onerror = (): void => reject(new Error('Erreur lors de la lecture du fichier'));
    reader.readAsText(file);
  });
};

/**
 * Formate une date pour l'affichage
 */
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString('fr-FR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Filtre les données selon une recherche textuelle
 */
export const filterBySearch = <T extends Entity>(
  data: T[], 
  searchTerm: string, 
  searchableFields: string[]
): T[] => {
  if (!searchTerm.trim()) return data;
  
  const term = searchTerm.toLowerCase();
  return data.filter((item: T): boolean => 
    searchableFields.some((field: string): boolean => {
      const value = (item as Record<string, unknown>)[field];
      return value != null && value.toString().toLowerCase().includes(term);
    })
  );
};

/**
 * Trie les données selon une colonne et direction
 */
export const sortData = <T extends Entity>(
  data: T[], 
  sortKey: string, 
  sortDirection: 'asc' | 'desc'
): T[] => {
  return [...data].sort((a: T, b: T): number => {
    const aValue = (a as Record<string, unknown>)[sortKey];
    const bValue = (b as Record<string, unknown>)[sortKey];
    
    // Gestion des valeurs nulles/undefined
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
    if (bValue == null) return sortDirection === 'asc' ? -1 : 1;
    
    // Comparaison selon le type
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.localeCompare(bValue, 'fr-FR');
      return sortDirection === 'asc' ? comparison : -comparison;
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    // Dates
    if (aValue instanceof Date && bValue instanceof Date) {
      return sortDirection === 'asc' 
        ? aValue.getTime() - bValue.getTime() 
        : bValue.getTime() - aValue.getTime();
    }
    
    // Fallback sur string
    const aStr = String(aValue);
    const bStr = String(bValue);
    const comparison = aStr.localeCompare(bStr, 'fr-FR');
    return sortDirection === 'asc' ? comparison : -comparison;
  });
};

/**
 * Génère un UUID simple pour les nouveaux éléments
 */
export const generateTempUUID = (): string => {
  return 'temp_' + Math.random().toString(36).substr(2, 9);
};

/**
 * Valide les données avant création/mise à jour
 */
export const validateAssetData = (data: Record<string, unknown>, requiredFields: string[]): string[] => {
  const errors: string[] = [];
  
  requiredFields.forEach((field: string): void => {
    if (!data[field] || (typeof data[field] === 'string' && !(data[field] as string).trim())) {
      errors.push(`Le champ "${field}" est requis`);
    }
  });
  
  return errors;
}; 